import type { ConversationUserAttributes } from '@/server/models'

import { SQL, type SQLFragment } from '@/client/query'

import ConversationQ from './ConversationQ'
import {
  type AssociationDefinitions,
  type IncludeAssociations,
  includeAssociationsToSql,
  makeQueryModelV2,
  type QueryOrder,
  queryOrderToSQL,
} from './common'
import UserQ from './UserQ'

const ConversationUserQ = {
  ...makeQueryModelV2<ConversationUserAttributes>('conversation_user')([
    'id',
    'user_id',
    'conversation_id',
    'created_at',
    'updated_at',
    'deleted_at',
  ]),

  associations: (): AssociationDefinitions => ({
    User: {
      query: UserQ,
      foreignKey: UserQ.id,
      sourceKey: ConversationUserQ.user_id,
    },
    Conversation: {
      query: ConversationQ,
      foreignKey: ConversationQ.id,
      sourceKey: ConversationUserQ.conversation_id,
    },
  }),

  where: (
    {
      id,
      userId,
      conversationId,
      include = [],
      order = null,
      paranoid = true,
    }: {
      id?: number | SQLFragment<number> | null
      userId?: number | SQLFragment<number> | null
      conversationId?: number | SQLFragment<number> | null
      include?: IncludeAssociations
      order?: QueryOrder | null
      paranoid?: boolean
    },
    customWhere: SQLFragment<unknown> | null = null
  ): SQLFragment<ConversationUserAttributes> => SQL`
        FROM ${ConversationUserQ}
        ${includeAssociationsToSql(ConversationUserQ, include)}
        WHERE
            ${id != null ? SQL`${ConversationUserQ.id} = ${id} AND` : SQL``}
            ${userId != null ? SQL`${ConversationUserQ.user_id} = ${userId} AND` : SQL``}
            ${
              conversationId != null
                ? SQL`${ConversationUserQ.conversation_id} = ${conversationId} AND`
                : SQL``
            }
            ${paranoid ? SQL`${ConversationUserQ.deleted_at} IS NULL AND` : SQL``}
            ${customWhere != null ? customWhere : SQL`TRUE`}
            ${queryOrderToSQL(order)}
        `,

  getConversationParticipant: ({
    conversationId,
    selfUserId,
    paranoid = false,
    limit,
  }: {
    conversationId: number | SQLFragment<number>
    selfUserId: number
    paranoid?: boolean
    limit?: number
  }): SQLFragment<ConversationUserAttributes> => SQL`
        FROM (
          SELECT DISTINCT ON (${ConversationUserQ.user_id}) ${ConversationUserQ}.*
          FROM ${ConversationUserQ}
          WHERE
            ${ConversationUserQ.conversation_id} = ${conversationId} AND
            ${ConversationUserQ.user_id} != ${selfUserId} AND
            ${paranoid ? SQL`${ConversationUserQ.deleted_at} IS NULL AND` : SQL``}
            TRUE
          ORDER BY ${ConversationUserQ.user_id}, ${ConversationUserQ.id} DESC
          ${limit != null ? SQL`LIMIT ${limit}` : SQL``}
        ) AS ${ConversationUserQ}
        ${includeAssociationsToSql(ConversationUserQ, [{ association: 'User', required: false }])}
      `,

  countConversationParticipants: ({
    conversationId,
    paranoid = true,
  }: {
    conversationId?: number | SQLFragment<number>
    paranoid?: boolean
  } = {}): SQLFragment<number> =>
    SQL`(SELECT COUNT(DISTINCT ${ConversationUserQ.user_id})
      ${ConversationUserQ.where({
        conversationId: conversationId ?? ConversationUserQ.conversation_id,
        paranoid,
      })}
    )`,
}

export default ConversationUserQ
